import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface ResetOptions {
  deleteContracts?: boolean;
  deleteProposalTemplates?: boolean;
  deleteDocumentAnalysis?: boolean;
  deleteFavorites?: boolean;
  deleteAllUserData?: boolean;
  userId?: string;
  olderThanDays?: number;
  confirmPhrase?: string;
}

export interface ResetResult {
  success: boolean;
  deleted: {
    contracts: number;
    proposalTemplates: number;
    contractDocuments: number;
    contractRequirements: number;
    contractSow: number;
    contractEvaluation: number;
    favorites: number;
  };
  errors: string[];
}

export interface DataStats {
  contracts: number;
  proposalTemplates: number;
  favorites: number;
  users: number;
}

export function useAdminReset() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current data statistics
  const getStats = useCallback(async (): Promise<DataStats> => {
    try {
      const [contractsRes, templatesRes, favoritesRes, profilesRes] = await Promise.all([
        supabase.from('sam_contracts').select('*', { count: 'exact', head: true }),
        supabase.from('proposal_templates').select('*', { count: 'exact', head: true }),
        supabase.from('contract_favorites').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true })
      ]);

      return {
        contracts: contractsRes.count || 0,
        proposalTemplates: templatesRes.count || 0,
        favorites: favoritesRes.count || 0,
        users: profilesRes.count || 0
      };
    } catch (e) {
      console.error('Failed to get stats:', e);
      return { contracts: 0, proposalTemplates: 0, favorites: 0, users: 0 };
    }
  }, []);

  // Reset data using the edge function
  const resetData = useCallback(async (options: ResetOptions): Promise<ResetResult> => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('admin-reset-data', {
        body: options
      });

      if (fnError) throw fnError;

      return data as ResetResult;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Reset failed';
      setError(message);
      return {
        success: false,
        deleted: {
          contracts: 0,
          proposalTemplates: 0,
          contractDocuments: 0,
          contractRequirements: 0,
          contractSow: 0,
          contractEvaluation: 0,
          favorites: 0
        },
        errors: [message]
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Quick reset: Delete all contracts and related data
  const resetAllContracts = useCallback(async (): Promise<ResetResult> => {
    return resetData({
      deleteContracts: true,
      deleteDocumentAnalysis: true,
      confirmPhrase: 'RESET DATA'
    });
  }, [resetData]);

  // Quick reset: Delete all proposals
  const resetAllProposals = useCallback(async (): Promise<ResetResult> => {
    return resetData({
      deleteProposalTemplates: true,
      confirmPhrase: 'RESET DATA'
    });
  }, [resetData]);

  // Full reset: Everything except user profiles
  const resetEverything = useCallback(async (): Promise<ResetResult> => {
    return resetData({
      deleteAllUserData: true,
      confirmPhrase: 'DELETE ALL DATA'
    });
  }, [resetData]);

  // Direct database operations for faster deletion
  // Deletes all contracts and related data using direct queries with WHERE clauses
  const directDeleteContracts = useCallback(async (): Promise<{ success: boolean; count: number; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      // Get count before delete
      const { count } = await supabase.from('sam_contracts').select('*', { count: 'exact', head: true });
      const contractCount = count || 0;

      console.log(`Starting deletion of ${contractCount} contracts and related data...`);

      // Delete in order respecting foreign key constraints
      // Use .gte on a timestamp or .neq on a string column that exists
      // Wrap each in try/catch to continue even if table doesn't exist

      // 1. Delete favorites (references sam_contracts)
      try {
        const { error: favError } = await supabase
          .from('contract_favorites')
          .delete()
          .gte('created_at', '1970-01-01');
        if (favError) console.log('Note: contract_favorites -', favError.message);
      } catch (e) {
        console.log('contract_favorites table may not exist');
      }

      // 2. Delete proposal templates (references sam_contracts)
      try {
        const { error: propError } = await supabase
          .from('proposal_templates')
          .delete()
          .gte('created_at', '1970-01-01');
        if (propError) console.log('Note: proposal_templates -', propError.message);
      } catch (e) {
        console.log('proposal_templates table may not exist');
      }

      // 3. Delete sam_documents (references sam_contracts by notice_id)
      try {
        const { error: samDocsError } = await supabase
          .from('sam_documents')
          .delete()
          .neq('notice_id', '');
        if (samDocsError) console.log('Note: sam_documents -', samDocsError.message);
      } catch (e) {
        console.log('sam_documents table may not exist');
      }

      // 4. Delete contract documents
      try {
        const { error: docsError } = await supabase
          .from('contract_documents')
          .delete()
          .gte('created_at', '1970-01-01');
        if (docsError) console.log('Note: contract_documents -', docsError.message);
      } catch (e) {
        console.log('contract_documents table may not exist');
      }

      // 5. Delete contract requirements
      try {
        const { error: reqError } = await supabase
          .from('contract_requirements')
          .delete()
          .gte('created_at', '1970-01-01');
        if (reqError) console.log('Note: contract_requirements -', reqError.message);
      } catch (e) {
        console.log('contract_requirements table may not exist');
      }

      // 6. Delete contract SOW
      try {
        const { error: sowError } = await supabase
          .from('contract_sow')
          .delete()
          .gte('created_at', '1970-01-01');
        if (sowError) console.log('Note: contract_sow -', sowError.message);
      } catch (e) {
        console.log('contract_sow table may not exist');
      }

      // 7. Delete contract evaluation (may not exist)
      try {
        const { error: evalError } = await supabase
          .from('contract_evaluation')
          .delete()
          .gte('created_at', '1970-01-01');
        if (evalError) console.log('Note: contract_evaluation -', evalError.message);
      } catch (e) {
        console.log('contract_evaluation table may not exist');
      }

      // 8. Finally delete all contracts - use notice_id which definitely exists
      const { error: contractsError } = await supabase
        .from('sam_contracts')
        .delete()
        .neq('notice_id', '');

      if (contractsError) {
        console.error('Error deleting contracts:', contractsError);
        throw contractsError;
      }

      console.log(`Successfully deleted ${contractCount} contracts and all related data`);
      return { success: true, count: contractCount };
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Delete failed';
      setError(message);
      console.error('Delete error:', e);
      return { success: false, count: 0, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    getStats,
    resetData,
    resetAllContracts,
    resetAllProposals,
    resetEverything,
    directDeleteContracts
  };
}
